/* STEP 7b · F6 (Card V2, 29-Oct-2026) — the analysis pages cover the engagement's financial year. This line names the period the
   page analysed and, when the run kept prior-year months apart, lists them under a clearly labelled "Prior year (comparison)"
   heading (collapsed). A result computed before the rule says so ("run the analysis again"). EN + AR (lib/i18n/1830_s7b_fixes.js).
   ``blocks`` = [{ key, title, rows: [{ key, month, value }] }] — the prior-year months of each check, built by the tab. */
import React from "react";
import { useI18n } from "@/contexts/I18nContext";
import { asArray } from "@/lib/asArray";

export default function FyPeriodNote({ period, blocks = [], testid = "fy-period" }) {
  const { t } = useI18n();
  if (!period || !period.label) return null;
  const pyRows = asArray(blocks).filter((b) => asArray(b.rows).length);
  const pyTitle = String(t("s7b.period.pyTitle") || "").replace("{start} – {end}", period.prior_label || "");
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-2 text-xs dark:border-slate-800 dark:bg-slate-900/40"
         data-testid={testid}>
      <div className="font-medium" data-testid={`${testid}-label`}>
        {String(t("s7b.period.fy") || "").replace("{start} – {end}", period.label)}
      </div>
      {period.recompute_needed && (
        <div className="mt-1 text-amber-700" data-testid={`${testid}-recompute`}>{t("s7b.period.oldRun")}</div>
      )}
      {pyRows.length > 0 && (
        <details className="mt-1" data-testid={`${testid}-py`}>
          <summary className="cursor-pointer text-muted-foreground">{t("s7b.period.pyNote")}</summary>
          <div className="mt-1 font-medium">{pyTitle}</div>
          <div className="mt-1 grid gap-2 sm:grid-cols-2">
            {pyRows.map((b) => (
              <div key={b.key} data-testid={`${testid}-py-${b.key}`}>
                <div className="text-muted-foreground">{b.title}</div>
                <table className="mt-0.5 text-[11px]">
                  <tbody>
                    {b.rows.map((r) => (
                      <tr key={r.key}><td className="pe-3">{r.month}</td><td className="text-end font-mono">{r.value}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

/** The prior-year blocks of a tab: for each check in ``checks`` ({key, labelKey}) whose ``spec[key]`` = [field, format], its
 *  ``prior_year_series`` as month / value rows. */
export function fyBlocks(doc, checks, t, spec, fmtMonth) {
  return asArray(checks).filter((c) => spec[c.key]).map((c) => {
    const [field, fmt] = spec[c.key];
    const rows = asArray(doc && doc[c.key] && doc[c.key].prior_year_series).map((r) => ({
      key: r.period_end || r.period, month: fmtMonth(String(r.period_end || r.period || "").slice(0, 7)),
      value: r[field] == null ? "—" : fmt(r[field]),
    }));
    return { key: c.key, title: t(c.labelKey), rows };
  });
}
